import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useConfig } from "./ConfigProvider.jsx";
import { useFeatureFlags } from "./FeatureFlagsProvider.jsx";

const Ctx = createContext({
  connected: false,
  last: null,
  subscribe: () => () => {},
  eventSource: null,
});

export function RealtimeProvider({ children }) {
  const cfgCtx = useConfig() || {};
  const config = cfgCtx.config || {};
  const { flagsSet } = useFeatureFlags() || { flagsSet: new Set() };

  // Gate with feature flag if you want (optional). Comment out to always connect when groupId exists.
  const realtimeEnabled = flagsSet.has("realtime") || false;

  const groupId = config?.groupId || ""; // no SSE until we know the location/group
  const esRef = useRef(null);
  const subsRef = useRef(new Set());
  const [connected, setConnected] = useState(false);
  const [last, setLast] = useState(null);

  const EVENTS_URL = "https://api.yebo-ai.com";

  useEffect(() => {
    if (!groupId || !realtimeEnabled) return;
    let es;
    try {
      es = new EventSource(`${EVENTS_URL}/api/health`);
    } catch (e) {
      // console.warn("[realtime] SSE not supported in this browser", e);
      return;
    }

    esRef.current = es;
    es.onopen = () => setConnected(true);
    es.onerror = () => setConnected(false);
    es.onmessage = (ev) => {
      const payload = safeParse(ev.data);
      const msg = {
        type: payload?.type || "message",
        data: payload?.data ?? payload ?? null,
        raw: ev,
      };
      setLast(msg);
      subsRef.current.forEach((fn) => {
        try {
          fn(msg);
        } catch {
          /* noop */
        }
      });
    };

    return () => {
      setConnected(false);
      try {
        es.close();
      } catch {
        /* noop */
      }
      esRef.current = null;
    };
  }, [groupId, realtimeEnabled]);

  const subscribe = (fn) => {
    subsRef.current.add(fn);
    return () => subsRef.current.delete(fn);
  };

  const value = useMemo(
    () => ({
      connected,
      last,
      subscribe,
      eventSource: esRef.current,
    }),
    [connected, last]
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

function safeParse(s) {
  try {
    return JSON.parse(s);
  } catch {
    return s;
  }
}

export function useRealtime() {
  return useContext(Ctx) || { connected: false, last: null, subscribe: () => () => {}, eventSource: null };
}

export default RealtimeProvider;
